import { type AdSet } from "~/ui-rtk/api/types/paid-media.types"
import { type AdForgeFilters } from "./validation-schema"
import AdSetList from "./AdSetList"

interface DestinationSectionProps {
  campaignSearchQuery: string
  onCampaignSearchChange: (value: string) => void
  onFiltersClick: () => void
  isLoadingAdSets: boolean
  filters: AdForgeFilters
  filteredAdSets: AdSet[]
  selectedAdSets: Record<string, boolean>
  onToggleAdSet: (adSetId: string) => void
  onShowCampaign: (campaignId: string) => void
  isLoadingMore: boolean
  hasMore: boolean
  onScroll: (e: React.UIEvent<HTMLDivElement>) => void
  scrollContainerRef: React.RefObject<HTMLDivElement>
}

export default function DestinationSection({
  campaignSearchQuery,
  onCampaignSearchChange,
  onFiltersClick,
  isLoadingAdSets,
  filters,
  filteredAdSets,
  selectedAdSets,
  onToggleAdSet,
  onShowCampaign,
  isLoadingMore,
  hasMore,
  onScroll,
  scrollContainerRef,
}: DestinationSectionProps) {
  return (
    <>
      {/* Scrollable Content Area */}
      <div
        className="flex-1 overflow-y-auto pl-6 pr-2"
        ref={scrollContainerRef}
        onScroll={onScroll}
      >
        {/* Destination Section */}
        <div className="mb-6 pb-6 border-b-2 border-system-blue-600">
          <h6 className="text-gray-400 text-sm font-medium mb-4 uppercase tracking-wide text-left">
            DESTINATION
          </h6>

          <div className="flex gap-3 mb-4">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search ad sets or campaign ID..."
                value={campaignSearchQuery}
                onChange={e => onCampaignSearchChange(e.target.value)}
                className="w-full px-4 py-2 bg-system-blue-700 border border-system-blue-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
              />
              <svg
                className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>

            {/* Filter Button */}
            <button
              onClick={onFiltersClick}
              className="px-4 py-2 bg-system-blue-700 border border-system-blue-600 rounded-lg text-white hover:bg-system-blue-600 focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent transition-colors flex items-center gap-2"
            >
              {isLoadingAdSets ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand"></div>
              ) : (
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                  />
                </svg>
              )}
              Filters
              {(filters.campaignFilter ||
                filters.statusFilter !== "ACTIVE" ||
                filters.minSpend ||
                filters.maxSpend) && (
                <span className="w-2 h-2 bg-brand rounded-full"></span>
              )}
            </button>
          </div>

          {/* Ad Sets List */}
          <AdSetList
            adSets={filteredAdSets}
            selectedAdSets={selectedAdSets}
            onToggleAdSet={onToggleAdSet}
            onShowCampaign={onShowCampaign}
            isLoadingAdSets={isLoadingAdSets}
            isLoadingMore={isLoadingMore}
            hasMore={hasMore}
            campaignSearchQuery={campaignSearchQuery}
            filters={filters}
          />
        </div>
      </div>
    </>
  )
}
